import React from 'react';
import {
  ICalcomEventType,
  ICreatePropertyBookingLinkRequest,
  IPropertyBookingLink,
  IUpdatePropertyBookingLinkRequest,
  IUserAssociatedProperty,
  JuniperEventType,
} from '../../types';
import BookingLinkPropertyCard from '../BookingLinkPropertyCard/BookingLinkPropertyCard';
import EventTypeRequiredCallout from '../EventTypeRequiredCallout/EventTypeRequiredCallout';
import BookingLinksManagerSkeleton from './BookingLinksManagerSkeleton/BookingLinksManagerSkeleton';

interface BookingLinksBodyProps {
  isLoading: boolean;
  error: string | null;
  hasNoEventTypes: boolean;
  properties: IUserAssociatedProperty[];
  eventTypes: ICalcomEventType[];
  linksFor: (
    propertyId: string,
    kind: JuniperEventType
  ) => IPropertyBookingLink[];
  isSubmitting: boolean;
  onCreate: (request: ICreatePropertyBookingLinkRequest) => Promise<boolean>;
  onUpdate: (
    bookingLinkId: string,
    request: IUpdatePropertyBookingLinkRequest
  ) => Promise<boolean>;
  onRemove: (bookingLinkId: string) => Promise<boolean>;
  deepLinkPropertyId?: string;
  deepLinkKind?: JuniperEventType;
}

const BookingLinksBody = ({
  isLoading,
  error,
  hasNoEventTypes,
  properties,
  eventTypes,
  linksFor,
  isSubmitting,
  onCreate,
  onUpdate,
  onRemove,
  deepLinkPropertyId,
  deepLinkKind = JuniperEventType.Walkthrough,
}: BookingLinksBodyProps) => {
  if (isLoading) {
    return <BookingLinksManagerSkeleton />;
  }

  if (error) {
    return <p className="error-text">{error}</p>;
  }

  if (hasNoEventTypes) {
    return <EventTypeRequiredCallout />;
  }

  if (properties.length === 0) {
    return (
      <p className="booking-links-muted">
        You don&apos;t manage any properties yet, so there are no booking links
        to set up. Reach out to a property developer or admin to be added to one.
      </p>
    );
  }

  const propertyCountLabel: string =
    properties.length === 1 ? 'property' : 'properties';

  return (
    <div className="booking-links-body">
      <div className="property-card-grid">
        {properties.map((property: IUserAssociatedProperty) => (
          <BookingLinkPropertyCard
            key={property.propertyId}
            property={property}
            eventTypes={eventTypes}
            linksFor={linksFor}
            isSubmitting={isSubmitting}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onRemove={onRemove}
            deepLinkPropertyId={deepLinkPropertyId}
            deepLinkKind={deepLinkKind}
          />
        ))}
      </div>

      <p className="booking-links-count">
        {properties.length} {propertyCountLabel}
      </p>
    </div>
  );
};

export default BookingLinksBody;